import { useEffect, useRef } from 'react';

export type Viewport = 'desktop' | 'tablet' | 'mobile';

/** Frame widths, matching the breakpoints the generated stylesheet uses. */
const VIEWPORT_WIDTH: Record<Viewport, number | null> = {
  desktop: null,
  tablet: 768,
  mobile: 375,
};

interface PreviewFrameProps {
  /** Full HTML document from the backend, in preview mode. */
  html: string;
  viewport: Viewport;
  /** Fired when the visitor edits a text node in place. */
  onEditText: (sectionId: string, field: string, value: string) => void;
  onSelectSection?: (sectionId: string) => void;
  busy?: boolean;
}

interface PreviewMessage {
  source?: string;
  type?: string;
  sectionId?: string;
  field?: string;
  value?: string;
}

/**
 * The page preview.
 *
 * Rendered with `srcdoc` rather than a URL so the auth token never lands in a
 * browsable address, and sandboxed without `allow-same-origin` so the page gets an
 * opaque origin — a generated page cannot reach into the app around it. Scripts stay
 * enabled because the editing bridge inside the document is what reports edits back.
 */
export function PreviewFrame({ html, viewport, onEditText, onSelectSection, busy }: PreviewFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  // Held in a ref so the listener does not need re-attaching on every render.
  const handlers = useRef({ onEditText, onSelectSection });
  handlers.current = { onEditText, onSelectSection };

  useEffect(() => {
    function onMessage(event: MessageEvent<PreviewMessage>) {
      const data = event.data;
      // The sandboxed frame has an opaque origin, so identify it by its own marker
      // and by the fact that it is the frame we rendered.
      if (!data || data.source !== 'builder-preview') return;
      if (frameRef.current && event.source !== frameRef.current.contentWindow) return;

      if (data.type === 'edit' && data.sectionId && data.field && typeof data.value === 'string') {
        handlers.current.onEditText(data.sectionId, data.field, data.value);
      } else if (data.type === 'select' && data.sectionId) {
        handlers.current.onSelectSection?.(data.sectionId);
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const width = VIEWPORT_WIDTH[viewport];

  return (
    // The frame scrolls internally rather than growing: at mobile width the preview
    // should behave like a phone screen, which is the point of switching viewports.
    <div className="flex-1 min-h-0 overflow-hidden bg-[var(--grey-accent,#eeeeee)] p-[16px] flex justify-center">
      <div
        className="relative h-full bg-white rounded-[12px] overflow-hidden shadow-sm transition-[width] duration-200 w-full"
        style={width ? { width, maxWidth: '100%' } : undefined}
      >
        <iframe
          ref={frameRef}
          title="preview"
          srcDoc={html}
          sandbox="allow-scripts allow-forms allow-popups"
          className="w-full h-full border-0 block"
        />
        {busy && (
          <div className="absolute inset-0 bg-white/60 pointer-events-none" aria-hidden="true" />
        )}
      </div>
    </div>
  );
}
